// Player settings, stored in localStorage. Stored data is untrusted (old
// versions, hand edits), so it is parsed with zod and every bad or missing
// field falls back to its default on its own.
import { z } from "zod";

export const SETTINGS_KEY = "wp.settings.v1";

export const TEXT_SCALES = [100, 125, 150] as const;
export type TextScale = (typeof TEXT_SCALES)[number];

export interface Settings {
  game: {
    reduceAnimations: boolean;
    reduceEffects: boolean;
    showSeatBadges: boolean;
    showTableInfo: boolean;
    showTurnTimer: boolean;
    showSpellbook: boolean;
    /** First Time mode: step-by-step tips for new players (on until turned off). */
    firstTime: boolean;
  };
  audio: {
    master: number;
    music: number;
    effects: number;
  };
  accessibility: {
    textScale: TextScale;
    photosensitive: boolean;
  };
}

export const DEFAULT_SETTINGS: Settings = {
  game: {
    reduceAnimations: false,
    reduceEffects: false,
    showSeatBadges: true,
    showTableInfo: true,
    showTurnTimer: true,
    showSpellbook: true,
    firstTime: true,
  },
  audio: { master: 0.8, music: 0.5, effects: 0.8 },
  accessibility: { textScale: 100, photosensitive: false },
};

const flag = (d: boolean) => z.boolean().catch(d);
const volume = (d: number) => z.number().min(0).max(1).catch(d);
const g = DEFAULT_SETTINGS.game;
const a = DEFAULT_SETTINGS.audio;
const x = DEFAULT_SETTINGS.accessibility;

export const SettingsSchema = z.object({
  game: z
    .object({
      reduceAnimations: flag(g.reduceAnimations),
      reduceEffects: flag(g.reduceEffects),
      showSeatBadges: flag(g.showSeatBadges),
      showTableInfo: flag(g.showTableInfo),
      showTurnTimer: flag(g.showTurnTimer),
      showSpellbook: flag(g.showSpellbook),
      firstTime: flag(g.firstTime),
    })
    .catch(g),
  audio: z.object({ master: volume(a.master), music: volume(a.music), effects: volume(a.effects) }).catch(a),
  accessibility: z
    .object({
      textScale: z.union([z.literal(100), z.literal(125), z.literal(150)]).catch(x.textScale),
      photosensitive: flag(x.photosensitive),
    })
    .catch(x),
});

/** Parse anything into valid settings (never throws). */
export function parseSettings(raw: unknown): Settings {
  const res = SettingsSchema.safeParse(raw ?? {});
  return res.success ? res.data : DEFAULT_SETTINGS;
}

type ReadStore = Pick<Storage, "getItem">;
type WriteStore = Pick<Storage, "setItem">;

export function loadSettings(store: ReadStore | null): Settings {
  if (!store) return DEFAULT_SETTINGS;
  try {
    const text = store.getItem(SETTINGS_KEY);
    return text ? parseSettings(JSON.parse(text)) : DEFAULT_SETTINGS;
  } catch {
    // Corrupt JSON or blocked storage.
    return DEFAULT_SETTINGS;
  }
}

export function saveSettings(store: WriteStore | null, settings: Settings): boolean {
  if (!store) return false;
  try {
    store.setItem(SETTINGS_KEY, JSON.stringify(settings));
    return true;
  } catch {
    return false; // quota / private mode
  }
}

export type SettingsPatch = {
  [K in keyof Settings]?: Partial<Settings[K]>;
};

export function mergeSettings(base: Settings, patch: SettingsPatch): Settings {
  return parseSettings({
    game: { ...base.game, ...patch.game },
    audio: { ...base.audio, ...patch.audio },
    accessibility: { ...base.accessibility, ...patch.accessibility },
  });
}

/** CSS classes toggled on <html> by settings. */
export function settingsClasses(s: Settings): Record<string, boolean> {
  return {
    "reduce-motion": s.game.reduceAnimations,
    "reduce-effects": s.game.reduceEffects || s.accessibility.photosensitive,
    photosensitive: s.accessibility.photosensitive,
    "hide-seat-badges": !s.game.showSeatBadges,
    "hide-table-info": !s.game.showTableInfo,
    "hide-turn-timer": !s.game.showTurnTimer,
    "hide-spellbook": !s.game.showSpellbook,
  };
}

/** Apply settings as CSS variables and classes on the root element. */
export function applySettings(s: Settings, root: HTMLElement): void {
  root.style.setProperty("--text-scale", String(s.accessibility.textScale / 100));
  for (const [cls, on] of Object.entries(settingsClasses(s))) root.classList.toggle(cls, on);
}

/** localStorage, or null when the browser blocks it. */
export function browserStorage(): Storage | null {
  try {
    return typeof window !== "undefined" ? window.localStorage : null;
  } catch {
    return null;
  }
}
